import { runRenameChatWorkflow } from "@/action-dialog";
import { restoreErrorMessage, useRestoreFlowSession } from "@/api/archived";
import {
  useDeleteFlowSession,
  useUpdateWorkspaceInboxItem,
} from "@/api/flow_sessions";
import { usePrivateWorkspace } from "@/api/workspaces";
import { showMovedToTrashToast } from "@/components/ConfirmTrashDialog/movedToTrashToast";
import { useOpenChat } from "@/hooks/useOpenChat";
import { useCreatePrivateWorkspaceChat } from "@/pages/workspaces/hooks/useChatCardActions";
import type { InboxItem } from "@cohesive/api-client";
import { useCallback } from "react";
import toast from "react-hot-toast";

export function useChatsRailActions() {
  const { openChat } = useOpenChat();
  const privateWorkspace = usePrivateWorkspace();
  const updateInboxItem = useUpdateWorkspaceInboxItem();

  const { create: createPrivateChat, isPending: isCreatingChat } =
    useCreatePrivateWorkspaceChat();

  const deleteFlowSession = useDeleteFlowSession({
    onError: () => toast.error("Failed to delete chat"),
  });
  const restoreFlowSession = useRestoreFlowSession({
    onError: (err) =>
      toast.error(restoreErrorMessage(err, "Failed to restore chat")),
  });

  const handleOpen = useCallback(
    (item: InboxItem) => {
      if (!item.is_read) {
        updateInboxItem.mutate({ id: item.id, is_read: true });
      }
      openChat(item.flow_session_id, { source: "rail" });
    },
    [openChat, updateInboxItem],
  );

  const handleToggleStar = useCallback(
    (item: InboxItem) => {
      updateInboxItem.mutate({
        id: item.id,
        is_starred: !item.is_starred,
      });
    },
    [updateInboxItem],
  );

  const handleRename = useCallback((item: InboxItem) => {
    window.setTimeout(() => void runRenameChatWorkflow({ item }), 0);
  }, []);

  const handleDelete = useCallback(
    (item: InboxItem) => {
      const flowSessionId = item.flow_session_id;
      deleteFlowSession.mutate(flowSessionId, {
        onSuccess: () =>
          showMovedToTrashToast(() => restoreFlowSession.mutate(flowSessionId)),
      });
    },
    [deleteFlowSession, restoreFlowSession],
  );

  const handleCreateChat = useCallback(() => {
    if (!privateWorkspace?.id) {
      toast.error("Private workspace not available");
      return;
    }
    createPrivateChat(privateWorkspace.id);
  }, [createPrivateChat, privateWorkspace?.id]);

  return {
    onOpen: handleOpen,
    onToggleStar: handleToggleStar,
    onRename: handleRename,
    onDelete: handleDelete,
    onCreateChat: handleCreateChat,
    isCreatingChat,
    canCreateChat: !!privateWorkspace?.id,
  };
}
